import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Users, Shield, Zap, TrendingUp, Target, Loader2 } from 'lucide-react';
import { config } from '@/lib/config';

// Partner form data interface
interface PartnerFormData {
  companyName: string;
  contactNameTitle: string;
  emailPhone: string;
  partnershipTrack: string;
  primaryUseCases: string;
  regionsServed: string;
  additionalInfo: string;
}

export function PartnerSection() {
  const { toast } = useToast();
  const [partnerLoading, setPartnerLoading] = useState(false);
  
  const [partnerFormData, setPartnerFormData] = useState<PartnerFormData>({
    companyName: '',
    contactNameTitle: '',
    emailPhone: '',
    partnershipTrack: '',
    primaryUseCases: '',
    regionsServed: '',
    additionalInfo: ''
  });

  const handlePartnerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!partnerFormData.companyName || !partnerFormData.contactNameTitle || !partnerFormData.emailPhone || !partnerFormData.partnershipTrack || !partnerFormData.primaryUseCases) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    setPartnerLoading(true);

    try {
      const response = await fetch(`${config.api.baseUrl}${config.api.endpoints.partner}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(partnerFormData),
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Success!",
          description: "✅ Your partner application has been submitted successfully! We'll be in touch soon.",
          variant: "default"
        });

        // Reset form
        setPartnerFormData({
          companyName: '',
          contactNameTitle: '',
          emailPhone: '',
          partnershipTrack: '',
          primaryUseCases: '',
          regionsServed: '',
          additionalInfo: ''
        });
      } else {
        toast({
          title: "Error",
          description: result.message || "❌ Failed to submit application. Please try again later.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error submitting partner form:', error);
      toast({
        title: "Error",
        description: "❌ Failed to submit application. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setPartnerLoading(false);
    }
  };

  return (
    <section className="py-24 bg-gradient-to-b from-background to-muted/20">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Partner Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent leading-tight pb-2">
              Partner with the AI Execution Engine
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Amplify your impact by bringing secure, action-driven automation to every enterprise stack.
            </p>
            <Button size="lg" className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90">
              <Users className="w-5 h-5 mr-2" />
              Become a Partner
            </Button>
          </div>

          {/* Why Partner */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold mb-8 text-center">Why Partner with NeurosEdge</h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="gradient-card rounded-xl p-6 text-center">
                <Shield className="w-12 h-12 text-primary mx-auto mb-4" />
                <h4 className="font-semibold mb-2">Execution Under Guardrails</h4>
                <p className="text-muted-foreground text-sm">Close the last mile from insight to action without risk</p>
              </div>
              <div className="gradient-card rounded-xl p-6 text-center">
                <Zap className="w-12 h-12 text-accent mx-auto mb-4" />
                <h4 className="font-semibold mb-2">Platform-Agnostic Reach</h4>
                <p className="text-muted-foreground text-sm">Integrate with any cloud, DevOps, or ITSM toolchain</p>
              </div>
              <div className="gradient-card rounded-xl p-6 text-center">
                <TrendingUp className="w-12 h-12 text-primary mx-auto mb-4" />
                <h4 className="font-semibold mb-2">New Revenue Streams</h4>
                <p className="text-muted-foreground text-sm">Co-sell premium connectors, managed execution services, and compliance packs</p>
              </div>
            </div>
          </div>

          {/* Partnership Tracks */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold mb-8 text-center">Partnership Tracks</h3>
            <div className="gradient-card rounded-xl p-8">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-4 px-4 font-semibold">Track</th>
                      <th className="text-left py-4 px-4 font-semibold">Ideal Profile</th>
                      <th className="text-left py-4 px-4 font-semibold">Benefits</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border/50">
                      <td className="py-4 px-4 font-medium text-primary">Go-to-Market Reseller (GTM)</td>
                      <td className="py-4 px-4 text-muted-foreground">VARs, solution providers</td>
                      <td className="py-4 px-4 text-muted-foreground">Deal registration, tiered margins, co-marketing funds</td>
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="py-4 px-4 font-medium text-primary">System Integrator (SI)</td>
                      <td className="py-4 px-4 text-muted-foreground">DevOps & SecOps consultancies</td>
                      <td className="py-4 px-4 text-muted-foreground">Early access to SDK, sandbox licenses, joint reference wins</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-4 font-medium text-primary">Technology Alliance</td>
                      <td className="py-4 px-4 text-muted-foreground">Observability, AIOps, ticketing, or cloud vendors</td>
                      <td className="py-4 px-4 text-muted-foreground">Joint roadmap, API/SDK support, co-engineered integrations</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* How to Engage */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold mb-8 text-center">How to Engage</h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-primary font-bold">1</span>
                </div>
                <h4 className="font-semibold mb-2">Apply via the Partner Intake Form</h4>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-accent font-bold">2</span>
                </div>
                <h4 className="font-semibold mb-2">Align on joint use cases & GTM</h4>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-primary font-bold">3</span>
                </div>
                <h4 className="font-semibold mb-2">Activate enablement, demo environments, and co-marketing</h4>
              </div>
            </div>
          </div>

          {/* Partner Intake Form */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold mb-8 text-center">Partner Intake Form</h3>
            <div className="gradient-card rounded-2xl p-8">
              <form onSubmit={handlePartnerSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Company Name *</label>
                    <Input
                      name="companyName"
                      value={partnerFormData.companyName}
                      onChange={(e) => setPartnerFormData(prev => ({ ...prev, companyName: e.target.value }))}
                      placeholder="Your company name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Contact Name / Title *</label>
                    <Input
                      name="contactNameTitle"
                      value={partnerFormData.contactNameTitle}
                      onChange={(e) => setPartnerFormData(prev => ({ ...prev, contactNameTitle: e.target.value }))}
                      placeholder="Your full name or title"
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Email / Phone *</label>
                    <Input
                      name="emailPhone"
                      value={partnerFormData.emailPhone}
                      onChange={(e) => setPartnerFormData(prev => ({ ...prev, emailPhone: e.target.value }))}
                      placeholder="your.email@company.com or +1 (555) 123-4567"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Partnership Track of Interest *</label>
                    <select
                      name="partnershipTrack"
                      value={partnerFormData.partnershipTrack}
                      onChange={(e) => setPartnerFormData(prev => ({ ...prev, partnershipTrack: e.target.value }))}
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                    >
                      <option value="">Select a track</option>
                      <option value="Go-to-Market Reseller (GTM)">Go-to-Market Reseller (GTM)</option>
                      <option value="System Integrator (SI)">System Integrator (SI)</option>
                      <option value="Technology Alliance">Technology Alliance</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Primary Use Case(s) *</label>
                  <Textarea
                    name="primaryUseCases"
                    value={partnerFormData.primaryUseCases}
                    onChange={(e) => setPartnerFormData(prev => ({ ...prev, primaryUseCases: e.target.value }))}
                    placeholder="Describe the main use cases you'd like to address with NeurosEdge"
                    rows={3}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Regions Served</label>
                  <Input
                    name="regionsServed"
                    value={partnerFormData.regionsServed}
                    onChange={(e) => setPartnerFormData(prev => ({ ...prev, regionsServed: e.target.value }))}
                    placeholder="e.g., North America, EMEA, APAC"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Anything Else We Should Know?</label>
                  <Textarea
                    name="additionalInfo"
                    value={partnerFormData.additionalInfo}
                    onChange={(e) => setPartnerFormData(prev => ({ ...prev, additionalInfo: e.target.value }))}
                    placeholder="Additional information about your company, goals, or questions"
                    rows={3}
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={partnerLoading}
                  className="w-full bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90"
                >
                  {partnerLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Target className="w-5 h-5 mr-2" />
                      Submit Partner Application
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
